import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  Timeline as TimelineIcon,
  TrendingUp as TrendingIcon,
  TrendingDown as TrendingDownIcon,
  Notifications as NotificationsIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Visibility as VisibilityIcon,
  PhoneAndroid as MobileIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';

const TemporalTracking = ({ business, websiteAnalysis }) => {
  const [trackingData, setTrackingData] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [isTracking, setIsTracking] = useState(false);

  // Charger les données de suivi depuis le localStorage
  useEffect(() => {
    const savedData = localStorage.getItem(`tracking_${business?.website}`);
    if (savedData) {
      setTrackingData(JSON.parse(savedData));
    }
  }, [business]);

  // Sauvegarder les données de suivi
  const saveTrackingData = (data) => {
    setTrackingData(data);
    localStorage.setItem(`tracking_${business.website}`, JSON.stringify(data));
  };

  // Ajouter une nouvelle analyse au suivi
  const addTrackingPoint = () => {
    if (!business || !websiteAnalysis) return;

    const newPoint = {
      date: new Date().toISOString(),
      overallScore: websiteAnalysis.overallScore,
      performance: websiteAnalysis.performance.performanceScore,
      security: websiteAnalysis.security.securityScore,
      seo: websiteAnalysis.seo.seoScore,
      mobile: websiteAnalysis.mobile.mobileScore,
      technical: websiteAnalysis.technical.technicalScore,
      accessibility: websiteAnalysis.accessibility.accessibilityScore,
      ecommerce: websiteAnalysis.ecommerce.ecommerceScore,
      criticalIssues: websiteAnalysis.criticalIssues
    };

    const updatedData = [...trackingData, newPoint];
    saveTrackingData(updatedData);
    generateAlerts(updatedData);
  };

  // Générer des alertes basées sur l'évolution
  const generateAlerts = (data) => {
    if (data.length < 2) return;

    const newAlerts = [];
    const latest = data[data.length - 1];
    const previous = data[data.length - 2];

    // Alerte de dégradation
    if (latest.overallScore < previous.overallScore) {
      newAlerts.push({
        type: 'warning',
        title: 'Dégradation détectée',
        message: `Le score global a baissé de ${previous.overallScore - latest.overallScore} points`,
        date: new Date().toISOString()
      });
    }

    // Alerte d'amélioration
    if (latest.overallScore > previous.overallScore) {
      newAlerts.push({
        type: 'success',
        title: 'Amélioration détectée',
        message: `Le score global a augmenté de ${latest.overallScore - previous.overallScore} points`,
        date: new Date().toISOString()
      });
    }

    // Alerte de problèmes critiques
    if (latest.criticalIssues > previous.criticalIssues) {
      newAlerts.push({
        type: 'error',
        title: 'Nouveaux problèmes critiques',
        message: `${latest.criticalIssues - previous.criticalIssues} nouveau(x) problème(s) critique(s) détecté(s)`,
        date: new Date().toISOString()
      });
    }

    // Alerte de performance
    if (latest.performance < 50 && previous.performance >= 50) {
      newAlerts.push({
        type: 'error',
        title: 'Performance critique',
        message: 'La performance est passée en zone critique (< 50)',
        date: new Date().toISOString()
      });
    }

    // Alerte de sécurité
    if (latest.security < 80 && previous.security >= 80) {
      newAlerts.push({
        type: 'error',
        title: 'Sécurité dégradée',
        message: 'La sécurité est passée sous le seuil recommandé (< 80)',
        date: new Date().toISOString()
      });
    }

    setAlerts([...alerts, ...newAlerts]);
  };

  // Calculer les tendances
  const calculateTrends = () => {
    if (trackingData.length < 2) return null;

    const latest = trackingData[trackingData.length - 1];
    const previous = trackingData[trackingData.length - 2];

    return {
      overall: {
        change: latest.overallScore - previous.overallScore,
        trend: latest.overallScore > previous.overallScore ? 'up' : 'down'
      },
      performance: {
        change: latest.performance - previous.performance,
        trend: latest.performance > previous.performance ? 'up' : 'down'
      },
      security: {
        change: latest.security - previous.security,
        trend: latest.security > previous.security ? 'up' : 'down'
      },
      seo: {
        change: latest.seo - previous.seo,
        trend: latest.seo > previous.seo ? 'up' : 'down'
      },
      mobile: {
        change: latest.mobile - previous.mobile,
        trend: latest.mobile > previous.mobile ? 'up' : 'down'
      }
    };
  };

  // Obtenir les statistiques
  const getStats = () => {
    if (trackingData.length === 0) return null;

    const scores = trackingData.map(d => d.overallScore);
    const latest = trackingData[trackingData.length - 1];

    return {
      currentScore: latest.overallScore,
      averageScore: Math.round(scores.reduce((a, b) => a + b, 0) / scores.length),
      bestScore: Math.max(...scores),
      worstScore: Math.min(...scores),
      totalAnalyses: trackingData.length,
      lastAnalysis: new Date(latest.date).toLocaleDateString()
    };
  };

  const trends = calculateTrends();
  const stats = getStats();

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TimelineIcon color="primary" />
        Suivi Temporel
      </Typography>

      <Typography variant="body1" sx={{ mb: 3 }}>
        Suivez l'évolution de votre site web dans le temps et recevez des alertes automatiques.
      </Typography>

      {/* Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="contained"
              startIcon={<TimelineIcon />}
              onClick={addTrackingPoint}
              disabled={!business || !websiteAnalysis}
            >
              Ajouter Point de Suivi
            </Button>
            <Typography variant="body2" color="text.secondary">
              {stats ? `${stats.totalAnalyses} analyse(s) effectuée(s)` : 'Aucune analyse enregistrée'}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Statistiques */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Card sx={{ bgcolor: 'primary.light', color: 'white' }}>
              <CardContent>
                <Typography variant="h4">{stats.currentScore}/100</Typography>
                <Typography variant="body2">Score Actuel</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ bgcolor: 'info.light', color: 'white' }}>
              <CardContent>
                <Typography variant="h4">{stats.averageScore}/100</Typography>
                <Typography variant="body2">Moyenne</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ bgcolor: 'success.light', color: 'white' }}>
              <CardContent>
                <Typography variant="h4">{stats.bestScore}/100</Typography>
                <Typography variant="body2">Meilleur Score</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ bgcolor: 'warning.light', color: 'white' }}>
              <CardContent>
                <Typography variant="h4">{stats.worstScore}/100</Typography>
                <Typography variant="body2">Pire Score</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tendances */}
      {trends && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              📈 Évolution Récente
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1">Score Global:</Typography>
                  <Chip
                    icon={trends.overall.trend === 'up' ? <TrendingIcon /> : <TrendingDownIcon />}
                    label={`${trends.overall.change > 0 ? '+' : ''}${trends.overall.change}`}
                    color={trends.overall.trend === 'up' ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1">Performance:</Typography>
                  <Chip
                    icon={trends.performance.trend === 'up' ? <TrendingIcon /> : <TrendingDownIcon />}
                    label={`${trends.performance.change > 0 ? '+' : ''}${trends.performance.change}`}
                    color={trends.performance.trend === 'up' ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1">Sécurité:</Typography>
                  <Chip
                    icon={trends.security.trend === 'up' ? <TrendingIcon /> : <TrendingDownIcon />}
                    label={`${trends.security.change > 0 ? '+' : ''}${trends.security.change}`}
                    color={trends.security.trend === 'up' ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Historique */}
      {trackingData.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              📊 Historique des Analyses
            </Typography>
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              {trackingData.slice().reverse().map((point, index) => (
                <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">
                      {new Date(point.date).toLocaleDateString()} - {new Date(point.date).toLocaleTimeString()}
                    </Typography>
                    <Chip 
                      label={`${point.overallScore}/100`}
                      color={point.overallScore < 50 ? 'error' : point.overallScore < 70 ? 'warning' : 'success'}
                      size="small"
                    />
                  </Box>
                  <Grid container spacing={1}>
                    <Grid item xs={6} md={2}>
                      <Typography variant="caption">Performance: {point.performance}</Typography>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <Typography variant="caption">Sécurité: {point.security}</Typography>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <Typography variant="caption">SEO: {point.seo}</Typography>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <Typography variant="caption">Mobile: {point.mobile}</Typography>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <Typography variant="caption">Problèmes: {point.criticalIssues}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Alertes */}
      {alerts.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NotificationsIcon />
              Alertes ({alerts.length})
            </Typography>
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              {alerts.slice().reverse().map((alert, index) => (
                <Alert 
                  key={index} 
                  severity={alert.type} 
                  sx={{ mb: 1 }}
                  action={
                    <Tooltip title="Marquer comme lue">
                      <IconButton size="small">
                        <CheckIcon />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <Box>
                    <Typography variant="subtitle2">{alert.title}</Typography>
                    <Typography variant="body2">{alert.message}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(alert.date).toLocaleString()}
                    </Typography>
                  </Box>
                </Alert>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Recommandations de suivi */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            💡 Recommandations de Suivi
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <InfoIcon color="info" />
              </ListItemIcon>
              <ListItemText 
                primary="Analysez régulièrement" 
                secondary="Effectuez une analyse complète chaque semaine pour suivre l'évolution"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <WarningIcon color="warning" />
              </ListItemIcon>
              <ListItemText 
                primary="Surveillez les alertes" 
                secondary="Réagissez rapidement aux dégradations de performance ou de sécurité"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <TrendingIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Mesurez les améliorations" 
                secondary="Suivez l'impact de vos optimisations sur les scores"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Fixez des objectifs" 
                secondary="Définissez des scores cibles et planifiez les actions pour les atteindre"
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TemporalTracking;